import { globalStyle, style, styleVariants } from '../layer.css.ts';
import { finePointerHover } from '../theme-v2/fine-pointer-hover.ts';
import { hairline } from '../theme-v2/hairline.ts';
import { themeV2 } from '../theme-v2/vars.ts';
import { borderRadius, fonts, space } from '../tokens';
import { typography } from '../typography';

export const buttonV2 = style({
  ...typography.labelMd,
  cursor: 'pointer',
  border: 0,
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: space.s8,
  minHeight: 32,
  padding: `0 ${space.s12}px`,
  position: 'relative',
  fontFamily: fonts.sans,
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  borderRadius: borderRadius.r8,
  transitionDuration: '0.15s',
  transitionProperty: 'background-color, color, box-shadow',
  userSelect: 'none',

  selectors: {
    '&:focus': {
      outline: 'none',
    },
    '&:focus-visible': {
      outline: `2px solid ${themeV2.color.border.focus}`,
      outlineOffset: '-1px',
    },
    '&[data-disabled]': {
      cursor: 'default',
    },
  },
});

globalStyle(`.${buttonV2} :where(svg)`, {
  width: 16,
  height: 16,
  flexShrink: 0,
});

export const buttonV2Variants = styleVariants({
  primary: {
    backgroundColor: themeV2.color.bg.inverse.default,
    color: themeV2.color.text.inverse,

    '@media': {
      [finePointerHover]: {
        selectors: {
          '&:hover:not([data-disabled])': {
            backgroundColor: themeV2.color.bg.inverse.hover,
          },
        },
      },
    },
    selectors: {
      '&:active:not([data-disabled])': {
        backgroundColor: themeV2.color.bg.inverse.active,
      },
      '&[data-disabled]': {
        backgroundColor: themeV2.color.bg.disabled,
        color: themeV2.color.text.disabled,
      },
    },
  },
  secondary: {
    backgroundColor: themeV2.color.bg.interactive.default,
    color: themeV2.color.text.tertiary,
    boxShadow: hairline(themeV2.color.border.default),

    '@media': {
      [finePointerHover]: {
        selectors: {
          '&:hover:not([data-disabled])': {
            backgroundColor: themeV2.color.bg.interactive.hover,
            boxShadow: hairline(themeV2.color.border.hover),
          },
        },
      },
    },
    selectors: {
      '&:active:not([data-disabled])': {
        backgroundColor: themeV2.color.bg.interactive.active,
        boxShadow: hairline(themeV2.color.border.strong),
      },
      '&[data-disabled]': {
        color: themeV2.color.text.disabled,
        boxShadow: hairline(themeV2.color.border.disabled),
      },
    },
  },
  text: {
    backgroundColor: themeV2.color.bg.interactive.default,
    color: themeV2.color.text.secondary,

    '@media': {
      [finePointerHover]: {
        selectors: {
          '&:hover:not([data-disabled])': {
            backgroundColor: themeV2.color.bg.interactive.hover,
            color: themeV2.color.text.primary,
          },
        },
      },
    },
    selectors: {
      '&:active:not([data-disabled])': {
        backgroundColor: themeV2.color.bg.interactive.default,
        color: themeV2.color.text.primary,
      },
      '&[data-disabled]': {
        color: themeV2.color.text.disabled,
      },
    },
  },
});

export type ButtonV2Variant = keyof typeof buttonV2Variants;

export const buttonV2Sizes = styleVariants({
  default: {},
  icon: {
    width: 32,
    height: 32,
    minWidth: 32,
    minHeight: 32,
    padding: 0,
  },
  'icon-sm': {
    width: 24,
    height: 24,
    minWidth: 24,
    minHeight: 24,
    padding: 0,
  },
});

export type ButtonV2Size = keyof typeof buttonV2Sizes;

export const buttonV2StartIcon = style({
  paddingLeft: space.s8,
});

export const buttonV2EndIcon = style({
  paddingRight: space.s8,
});

export const buttonV2Class = styleVariants(buttonV2Variants, (variant) => [
  buttonV2,
  variant,
]);
